/**
 * DuplicateComparisonPanel — Phase R10.4 (2026-07-05)
 *
 * Side-by-side comparison of the current flagged claim against one of
 * the N matching claims from `claim.dedup_matching_claim_ids`.
 *
 * Renders ONLY when:
 *   - claim.dedup_flag ∈ {yellow, orange, red}, AND
 *   - claim.dedup_matching_claim_ids has >= 1 id
 *
 * Props:
 *   claim         — current claim (owned fields: ai_extraction,
 *                   customer, customer_user_id, branch_id, status,
 *                   created_at, image_url, image_mime,
 *                   dedup_matching_claim_ids, dedup_flag_reason)
 *   matchFetcher  — async (claimId) → Promise<claim | null>
 *                   (parent injects the api.ownerGetReceiptClaim closure)
 *   branches      — array of {id, name} for branch-name resolution
 *   token         — JWT token for authed image fetch
 *   apiBase       — API base URL for the image fetch
 *   forwardRef    — optional ref so parent can scrollIntoView()
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Loader2, AlertTriangle, GitCompare,
} from 'lucide-react';
import StatusPillSmall from './_StatusPillSmall';

function fmtRupiah(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const s = Math.round(Number(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `Rp ${s}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('id-ID', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function branchName(branchId, branches) {
  if (!branchId) return 'Unassigned';
  return branches.find((b) => b.id === branchId)?.name ?? 'Unknown branch';
}

const FIELD_ROWS = [
  { key: 'customer',   label: 'Customer' },
  { key: 'branch',     label: 'Branch' },
  { key: 'date',       label: 'Date' },
  { key: 'receipt_no', label: 'Receipt No.' },
  { key: 'total',      label: 'Total Amount' },
  { key: 'table',      label: 'Table' },
  { key: 'order_type', label: 'Order Type' },
  { key: 'cashier',    label: 'Cashier' },
  { key: 'submitted',  label: 'Submission Time' },
  { key: 'status',     label: 'Status' },
];

function extractRowValues(c, branches) {
  const ai = c?.ai_extraction ?? {};
  return {
    customer: {
      name: c?.customer?.name ?? null,
      memberId: c?.customer?.membership_id
        ?? (c?.customer_user_id ? `${c.customer_user_id.slice(0, 8)}…` : null),
    },
    branch:     branchName(c?.branch_id, branches),
    date:       ai.date ?? null,
    receipt_no: ai.receipt_no ?? null,
    total:      ai.total_amount != null ? fmtRupiah(ai.total_amount) : null,
    totalRaw:   ai.total_amount ?? null,
    table:      ai.table ?? null,
    order_type: ai.order_type ?? null,
    cashier:    ai.cashier ?? null,
    submitted:  c?.created_at ?? null,
    status:     c?.status ?? null,
  };
}

export default function DuplicateComparisonPanel({
  claim,
  matchFetcher,
  branches = [],
  token,
  apiBase,
}) {
  const [index, setIndex] = useState(0);
  const [matchClaim, setMatchClaim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [thisImg, setThisImg] = useState(null);
  const [matchImg, setMatchImg] = useState(null);

  const ids = Array.isArray(claim?.dedup_matching_claim_ids)
    ? claim.dedup_matching_claim_ids.filter(Boolean)
    : [];
  const total = ids.length;
  const currentMatchId = ids[index] ?? null;

  // Cache fetched matches so clicking Prev/Next doesn't re-fetch.
  const cacheRef = useRef({});

  // Reset the carousel if the matching set changes.
  const idsKey = ids.join('|');
  useEffect(() => {
    cacheRef.current = {};
    setIndex(0);
  }, [idsKey]);

  useEffect(() => {
    if (!currentMatchId) {
      setMatchClaim(null);
      return undefined;
    }
    let cancelled = false;
    const cached = cacheRef.current[currentMatchId];
    if (cached) {
      setMatchClaim(cached);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await matchFetcher(currentMatchId);
        if (cancelled) return;
        if (!data) {
          setError('Unable to load match — may be deleted or inaccessible.');
          setMatchClaim(null);
          return;
        }
        cacheRef.current[currentMatchId] = data;
        setMatchClaim(data);
      } catch {
        if (!cancelled) {
          setError('Unable to load match — may be deleted or inaccessible.');
          setMatchClaim(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentMatchId, matchFetcher]);

  // Fetch the "this" and "match" image blobs via Bearer auth so <img>
  // doesn't need a cookie. Mirrors the pattern used in the parent page.
  useEffect(() => {
    let revoke = null;
    (async () => {
      if (!claim?.image_url || !token || !apiBase) return;
      try {
        const res = await fetch(`${apiBase}${claim.image_url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setThisImg(url);
      } catch { /* best-effort */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [claim?.image_url, token, apiBase]);

  useEffect(() => {
    let revoke = null;
    (async () => {
      if (!matchClaim?.image_url || !token || !apiBase) return;
      try {
        const res = await fetch(`${apiBase}${matchClaim.image_url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setMatchImg(url);
      } catch { /* best-effort */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [matchClaim?.image_url, token, apiBase]);

  const thisRow = useMemo(() => extractRowValues(claim, branches), [claim, branches]);
  const matchRow = useMemo(
    () => (matchClaim ? extractRowValues(matchClaim, branches) : null),
    [matchClaim, branches],
  );

  const canPrev = index > 0;
  const canNext = index < total - 1;

  if (total === 0) return null;

  return (
    <section
      className="rounded-2xl border border-white/10 bg-[#15151B] p-5"
      data-testid="duplicate-comparison-panel"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2 text-white">
          <GitCompare size={16} className="text-gold" />
          Duplicate Comparison
        </h3>
        {total > 1 ? (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => canPrev && setIndex((i) => i - 1)}
              disabled={!canPrev}
              aria-label="Previous match"
              data-testid="duplicate-comparison-prev"
              className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-gold/60 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span
              className="text-white/75 font-medium whitespace-nowrap"
              data-testid="duplicate-comparison-match-counter"
            >
              Showing match {index + 1} of {total}
            </span>
            <button
              type="button"
              onClick={() => canNext && setIndex((i) => i + 1)}
              disabled={!canNext}
              aria-label="Next match"
              data-testid="duplicate-comparison-next"
              className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-gold/60 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        ) : null}
      </header>

      {claim?.dedup_flag_reason ? (
        <p
          className="text-sm text-white/70 leading-relaxed mb-4"
          data-testid="duplicate-comparison-reason"
        >
          {claim.dedup_flag_reason}
        </p>
      ) : null}

      {loading ? (
        <div
          className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm"
          data-testid="duplicate-comparison-loading"
        >
          <Loader2 size={16} className="animate-spin text-gold" />
          Loading matching claim…
        </div>
      ) : null}

      {error ? (
        <div
          className="py-6 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-200"
          data-testid="duplicate-comparison-error"
        >
          <AlertTriangle size={14} className="text-red-300 mt-0.5 shrink-0" />
          {error}
        </div>
      ) : null}

      {!loading && !error && matchRow ? (
        <>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold" data-testid="duplicate-comparison-this-header">
              This Claim
            </div>
            <div className="text-[10px] uppercase tracking-widest text-gold font-semibold" data-testid="duplicate-comparison-match-header">
              Matching Claim #{index + 1}
            </div>
          </div>

          <div className="divide-y divide-white/5">
            {FIELD_ROWS.map((row) => (
              <ComparisonRow
                key={row.key}
                label={row.label}
                keyName={row.key}
                leftVal={thisRow[row.key]}
                rightVal={matchRow[row.key]}
                leftExtras={row.key === 'customer' ? thisRow.customer : null}
                rightExtras={row.key === 'customer' ? matchRow.customer : null}
              />
            ))}
          </div>

          {/* Receipt image side-by-side */}
          <div className="grid grid-cols-2 gap-4 mt-5" data-testid="duplicate-comparison-images">
            <ImageTile label="This Claim" src={thisImg} testID="duplicate-comparison-image-this" />
            <ImageTile label={`Match #${index + 1}`} src={matchImg} testID="duplicate-comparison-image-match" />
          </div>
        </>
      ) : null}
    </section>
  );
}

function ComparisonRow({ label, keyName, leftVal, rightVal, leftExtras, rightExtras }) {
  // Determine match for highlighting. Compare on raw primitives.
  let leftDisp = leftVal;
  let rightDisp = rightVal;
  let isMatch = false;

  if (keyName === 'customer') {
    const leftName  = leftExtras?.name ?? null;
    const rightName = rightExtras?.name ?? null;
    const leftId    = leftExtras?.memberId ?? null;
    const rightId   = rightExtras?.memberId ?? null;
    isMatch = leftId != null && leftId === rightId;
    leftDisp = (
      <div className="flex flex-col gap-0.5">
        <span>{leftName || '—'}</span>
        <span className="font-mono text-[10px] text-white/50">{leftId || '—'}</span>
      </div>
    );
    rightDisp = (
      <div className="flex flex-col gap-0.5">
        <span>{rightName || '—'}</span>
        <span className="font-mono text-[10px] text-white/50">{rightId || '—'}</span>
      </div>
    );
  } else if (keyName === 'status') {
    isMatch = leftVal != null && leftVal === rightVal;
    leftDisp  = leftVal  ? <StatusPillSmall status={leftVal}  /> : '—';
    rightDisp = rightVal ? <StatusPillSmall status={rightVal} /> : '—';
  } else if (keyName === 'submitted') {
    const l = fmtDateTime(leftVal);
    const r = fmtDateTime(rightVal);
    isMatch = l !== '—' && l === r;
    leftDisp = l;
    rightDisp = r;
  } else {
    isMatch = leftVal != null && leftVal === rightVal;
    leftDisp  = leftVal  != null && leftVal  !== '' ? leftVal  : '—';
    rightDisp = rightVal != null && rightVal !== '' ? rightVal : '—';
  }

  const cellCls =
    'px-3 py-2 text-sm rounded-lg transition-colors ' +
    (isMatch
      ? 'bg-amber-500/10 text-amber-100 border border-amber-500/25'
      : 'bg-white/[0.02] text-white/85 border border-transparent');
  const monoKeys = ['receipt_no', 'total', 'table', 'cashier'];
  const monoCls = monoKeys.includes(keyName) ? 'font-mono text-xs' : '';

  return (
    <div
      className="grid grid-cols-[auto,1fr,1fr] gap-2 py-1.5 items-center"
      data-testid={`duplicate-comparison-row-${keyName}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold w-28">
        {label}
      </div>
      <div className={`${cellCls} ${monoCls}`} data-testid={`duplicate-comparison-${keyName}-this`}>
        {leftDisp}
      </div>
      <div className={`${cellCls} ${monoCls}`} data-testid={`duplicate-comparison-${keyName}-match`}>
        {rightDisp}
      </div>
    </div>
  );
}

function ImageTile({ label, src, testID }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black overflow-hidden">
      <div className="px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-white/50 font-semibold">
        {label}
      </div>
      <div className="aspect-[3/4] grid place-items-center">
        {src ? (
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="block w-full h-full"
            data-testid={`${testID}-link`}
          >
            <img
              src={src}
              alt={label}
              className="w-full h-full object-contain"
              data-testid={testID}
            />
          </a>
        ) : (
          <div className="flex flex-col items-center gap-1 text-white/40 text-[10px]">
            <Loader2 size={16} className="animate-spin" />
            Loading…
          </div>
        )}
      </div>
    </div>
  );
}
